import { notify, colors, firebaseErrors, strings, text } from "@quickpoint.me/shared";
import {
	getAuth,
	sendPasswordResetEmail,
	signInWithEmailAndPassword,
} from "firebase/auth";
import { useRef, useState } from "react";
import {
	useRecoilCallback,
	useRecoilState,
	useRecoilValue,
	useSetRecoilState,
} from "recoil";
import styled from "styled-components";
import {
	AuthScreenAtom,
	FirebaseErrorAtom,
	FirebaseUser,
} from "../../state/authAtoms";
import { AuthPillButton, Banner, StyledError } from "./styled/Components";

import type { FirebaseError } from "firebase/app";
import { cloneDeep } from "lodash-es";
import { useForm } from "react-hook-form";
import Turnstile from "react-turnstile";
import { getTurnstileSiteKey } from "../../lib/getTurnstileSiteKey";
import parseFirebaseErrors from "../../lib/parseFirebaseErrors";
import { LoginSuccessfulResetState } from "../../state/authSelectors";
import { IsInPortrait } from "../../state/selectors";
import { ShowRefreshSpinner } from "../../state/uiAtoms";
import { TermsOfService } from "./register/SignUp";
import { StyledInput, authInputCss } from "./styled/StyledInput";
import { StyledWrapper } from "./styled/StyledWrapper";

type LoginFormValues = {
	email: string;
	password: string;
};

export const StyledForm = styled.div`
  align-items: flex-start;
  display: flex;
  justify-self: center;
  width: 100%;
  justify-content: center;

  .invalid {
    color: ${colors.textSafe.red};
  }

  ${authInputCss}

  .email-message {
    margin-block: .5rem;
    font-size: 2rem;
  }

  .content {
    font-family: inherit;
    text-align: center;
    position: relative;
    z-index: 6;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    form {
      width: min(24rem, 100%);
    }
  }
`;

const LoginWithEmail = () => {
	const setShowRefreshSpinner = useSetRecoilState(ShowRefreshSpinner);
	const setFireBaseUser = useSetRecoilState(FirebaseUser);
	const setLoginSuccessfulResetState = useSetRecoilState(
		LoginSuccessfulResetState,
	);
	const [firebaseError, setFirebaseError] = useRecoilState(FirebaseErrorAtom);
	const [requestPasswordReset, setRequestPasswordReset] =
		useState<boolean>(false);
	const formRef = useRef<HTMLFormElement>(null);
	const isInPortrait = useRecoilValue(IsInPortrait);

	const {
		register,
		handleSubmit,
		watch,
		formState: { errors },
	} = useForm<LoginFormValues>();
	const [showSignUpLink, setShowSignUpLink] = useState(false);
	// the dev/test Turnstile key ("1x…") always passes, so don't gate login on
	// the external widget loading — if the script fails, login would be
	// permanently disabled in dev
	const usingTurnstileTestKey = getTurnstileSiteKey().startsWith("1x");
	const [disabled, setDisabled] = useState(!usingTurnstileTestKey);

	const resetPassword = async () => {
		setFirebaseError("");
		const auth = getAuth();

		const email = watch("email");
		if (!email) {
			notify(text.auth.enterEmailAlert);
			return;
		}

		try {
			await sendPasswordResetEmail(auth, email, {
				url: `https://author.quickpoint.me/${strings.common.unauthorized}/${strings.url.userRequestsLoginWithEmail}}`,
			});
			setRequestPasswordReset(true);
		} catch (e) {
			if (parseFirebaseErrors(e)) {
				setFirebaseError(parseFirebaseErrors(e));
			}
		}
	};

	const signMeUp = useRecoilCallback(({ set }) => () => {
		setFirebaseError("");
		set(AuthScreenAtom, "signUp");
	});

	const onSubmit = useRecoilCallback(
		() =>
			async ({ email, password }: LoginFormValues) => {
				setFirebaseError("");
				setShowRefreshSpinner(true);
				setShowSignUpLink(false);
				const auth = getAuth();
				try {
					const credential = await signInWithEmailAndPassword(
						auth,
						email,
						password,
					);

					if (credential) {
						// The signed-in user info.
						const user = credential.user;
						const recoilUser = cloneDeep(user);
						setFireBaseUser(recoilUser);
						setLoginSuccessfulResetState(true); // resets ShowTestCard atom
					}
				} catch (error: unknown) {
					setShowRefreshSpinner(false);
					setFirebaseError(text.auth.genericError);

					for (const code in firebaseErrors) {
						const e = error as FirebaseError;
						if (e.toString().includes(code)) {
							if (parseFirebaseErrors(error)) {
								if (e.code === "auth/user-not-found") {
									setShowSignUpLink(true);
								}
								setFirebaseError(parseFirebaseErrors(error));
							}
						}
					}
				}
			},
	);

	const submit = () => {
		setFirebaseError("");
		if (formRef.current) {
			formRef.current.dispatchEvent(
				new Event("submit", { cancelable: true, bubbles: true }),
			);
		}
	};

	const goBack = useRecoilCallback(({ set }) => () => {
		setFirebaseError("");
		set(AuthScreenAtom, "loginChoices");
	});

	return (
		<StyledInput $isInPortrait={isInPortrait} data-testid="login-with-email">
			<Banner />
			<StyledForm>
				<div className="content">
					<form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
						<StyledWrapper>
							<label className="owner-email-label" htmlFor="email">
								{text.auth.emailLabel}
							</label>
							<input
								placeholder={text.auth.emailPlaceholder}
								autoComplete="username"
								onKeyDown={(e) => {
									if (e.key === "Enter") submit();
								}}
								aria-invalid={errors.email ? "true" : "false"}
								className="auth-input auth-owner-email-input input-box-as-line"
								id="email"
								type="email"
								{...register("email", {
									required: true,
									pattern: {
										value: /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,10})+$/,
										message: " ",
									},
								})}
							/>
						</StyledWrapper>
						<StyledError>
							{errors.email && errors.email.type === "required" && (
								<span role="alert">{text.auth.emailRequired}</span>
							)}
							{errors.email && errors.email.type === "pattern" && (
								<span role="alert">{text.auth.emailInvalid}</span>
							)}
						</StyledError>
						<StyledWrapper>
							<label className="owner-password-label" htmlFor="password">
								{text.auth.passwordLabel}
							</label>
							<input
								placeholder={text.auth.passwordPlaceholder}
								aria-invalid={errors.password ? "true" : "false"}
								autoComplete="current-password"
								className="auth-input auth-owner-password-input input-box-as-line"
								onKeyDown={(e) => {
									if (e.key === "Enter") submit();
								}}
								id="password"
								type="password"
								{...register("password", { required: true })}
							/>
						</StyledWrapper>
						<StyledError>
							{errors.password && errors.password.type === "required" && (
								<span role="alert">{text.auth.passwordRequired}</span>
							)}
							{firebaseError && (
								<StyledError role="alert">{firebaseError}</StyledError>
							)}
						</StyledError>
					</form>
				</div>
			</StyledForm>

			<div className="buttons">
				<Turnstile
					sitekey={getTurnstileSiteKey()}
					style={{
						transition: disabled ? "none" : "opacity 1s ease-out",
						opacity: disabled ? 1 : 0.3,
					}}
					theme="light"
					fixedSize={true}
					onVerify={() => {
						setDisabled(false);
					}}
				/>
				{firebaseError && showSignUpLink && (
					<AuthPillButton $variant="dark" onClick={() => signMeUp()}>
						{text.auth.signUp}
					</AuthPillButton>
				)}
				<AuthPillButton
					id="auth-login-button"
					disabled={disabled}
					data-testid="email-login-button"
					onClick={submit}
				>
					{text.auth.login}
				</AuthPillButton>
				<AuthPillButton $variant="primaryOutline" onClick={() => goBack()}>
					{text.auth.back}
				</AuthPillButton>
				<AuthPillButton $variant="primaryOutline" onClick={resetPassword}>
					{text.auth.resetPassword}
				</AuthPillButton>

				{requestPasswordReset && (
					<StyledError role="alert">
						{text.auth.passwordResetSent}
					</StyledError>
				)}
				<TermsOfService />
			</div>
		</StyledInput>
	);
};

export default LoginWithEmail;
